'use client';

import { FC } from 'react';

import { MappedOrderHeader, OrderBaseUrls, OrderDocumentResponse, TextData } from '@ravago/shared/page-data/models/elements';
import { RendererGlobals } from '@ravago/shared/page-data/models/utils';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Divider } from '@ravago/shared/radiance/components/divider/components/Divider/Divider';

import { hasOrderDocuments } from '../../../../utils/order/order-helper.utils';
import { OrderDocuments } from '../../../shared/order-documents/order-documents';
import { OrderPageHeader } from '../../../shared/order-page-header/order-page-header';
import { DeliveryInformation } from '../../../shared/order/delivery-information';
import { InvoiceInformation } from '../../../shared/order/invoice-information';
import { OrderDetailsOrderLines } from '../../../shared/order/order-details-order-lines';
import { OrderShipments } from '../../../shared/order/order-shipments';

export type Props = {
   globals: RendererGlobals;
   order: MappedOrderHeader;
   orderDocuments: OrderDocumentResponse | undefined;
   showVatCode: boolean;
   showInvoiceDocuments: boolean;
   titleData: TextData;
   orderBaseUrls: OrderBaseUrls;
   isUSBusinessContext: boolean;
};

export const OrderDetails: FC<Props> = ({
   globals: {
      locale,
      acceptWebp,
      isConsumer,
      actions: { requestBol, requestCoa, requestDeliveryNote, downloadDocument }
   },
   titleData,
   order,
   orderDocuments,
   showVatCode,
   showInvoiceDocuments,
   orderBaseUrls,
   isUSBusinessContext
}) => {
   const showOrderDocuments = orderDocuments && hasOrderDocuments(orderDocuments);

   return (
      <Box direction="column">
         <OrderPageHeader headerText={titleData} id={order.id.toString()} status={order.status} date={undefined} />
         <OrderShipments
            isConsumer={isConsumer}
            acceptWebp={acceptWebp}
            locale={locale}
            orderDetail={order.mappedOrders}
            openedFrom="order_detail"
            isUSBusinessContext={isUSBusinessContext}
            showInvoiceDocuments={showInvoiceDocuments}
            orderBaseUrls={orderBaseUrls}
            requestCoa={requestCoa}
            requestDeliveryNote={requestDeliveryNote}
            requestBol={requestBol}
            downloadDocument={downloadDocument}
         />
         <OrderDetailsOrderLines
            isConsumer={isConsumer}
            acceptWebp={acceptWebp}
            locale={locale}
            openOrderLines={order.mappedOrders.openOrderLines}
            closedOrderLines={order.mappedOrders.closedOrderLines}
            orderBaseUrls={orderBaseUrls}
            showInvoiceDocuments={showInvoiceDocuments}
            openedFrom="order_detail"
            requestCoa={requestCoa}
            requestBol={requestBol}
            requestDeliveryNote={requestDeliveryNote}
            downloadDocument={downloadDocument}
            isUSBusinessContext={isUSBusinessContext}
         />
         {order.invoiceInfo && (
            <>
               <Divider />
               <InvoiceInformation vatCode={order.invoiceInfo.vatCode} invoiceEntity={order.invoiceInfo.ole} showVatCode={showVatCode} />
            </>
         )}
         {order.deliveryInfo && (
            <>
               <Divider />
               <DeliveryInformation
                  destinationAddress={order.deliveryInfo.destinationAddress}
                  deliveryTermAddress={order.deliveryInfo.deliveryTermAddress}
                  deliveryInstructions={order.deliveryInfo.deliveryInstructions}
                  deliveryTerm={order.deliveryInfo.deliveryTerm}
               />
            </>
         )}
         {showOrderDocuments && (
            <>
               <Divider />
               <OrderDocuments
                  isUSBusinessContext={isUSBusinessContext}
                  documents={orderDocuments}
                  orders={order}
                  showInvoiceDocuments={showInvoiceDocuments}
                  requestCoa={requestCoa}
                  requestBol={requestBol}
                  requestDeliveryNote={requestDeliveryNote}
                  downloadDocument={downloadDocument}
               />
            </>
         )}
      </Box>
   );
};
